import { Navigate, useLocation } from "react-router-dom";
import GPDRModal from "src/components/shared/_Modals/GPDRModal/GPDRModal";
import Header from "src/components/shared/Header/Header";
import { refreshLogoutTimerForHandover } from "src/generator/utils/refreshLogoutTimer";
import {
  useCheckPerm,
  useExpiredSessionRedirect,
  useIsOnline,
} from "src/hooks";
import { type IApiAuthData } from "src/interfaces";
import { onlyOnlineRoutes } from "src/mock/onlineOnlyRoutes";
import { getApiAuthData, setApiAuthData } from "src/utils";
import { Notification } from "src/utils/Notification";

import { DesktopSyncProvider } from "../hooks";
import ProtectedRouteContent from "./ProtectedRouteContent";

const parseHandoverFromHash = (): {
  auth: IApiAuthData;
  redirect: string | null;
} | null => {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) return null;

  const params = new URLSearchParams(hash.slice(queryIndex + 1));
  if (params.get("handover") !== "true") return null;

  const auth: IApiAuthData = {
    _userid: decodeURIComponent(params.get("userid") ?? ""),
    _yachtid: decodeURIComponent(params.get("yachtid") ?? ""),
    _session: decodeURIComponent(params.get("sid") ?? ""),
    _code: "",
  };

  let redirect = params.get("redirect");
  if (redirect) {
    redirect = decodeURIComponent(redirect);
    if (!redirect.startsWith("/")) {
      redirect = `/${redirect}`;
    }
  }

  return { auth, redirect };
};

export default function ProtectedRoute() {
  const { _userid } = getApiAuthData();
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const { pathname } = useLocation();
  const isSessionExpired = useExpiredSessionRedirect();

  // Login Code Handover
  // When a client uses there own API to login this code handles the auth data handover to log them via there own API
  const handover = parseHandoverFromHash();
  if (handover) {
    refreshLogoutTimerForHandover();
    setApiAuthData(handover.auth);

    if (handover.redirect) {
      return <Navigate to={handover.redirect} replace />;
    }

    return <Navigate to="/home" replace />;
  }

  if (isSessionExpired) {
    return <Navigate to="/login" replace />;
  }
  if (!_userid && !pathname.includes("/exports")) {
    return <Navigate to="/login" replace />;
  }
  if (!isOnline && onlyOnlineRoutes.some(route => pathname.includes(route))) {
    Notification(
      "Offline Redirect",
      "You appeared to be offline. Unfortunately previous view works only online",
      "yellow",
    );
    return <Navigate to="/" replace />;
  }

  if (
    (pathname === "/settings/userlist/create-user" &&
      !checkPerm("createuser")) ||
    (pathname === "/summary/reviews/create-review" && !checkPerm("review")) ||
    (pathname === "/summary/audits/create-audit" && !checkPerm("audit"))
  ) {
    Notification(
      "Unauthorized",
      "You do not have the appropriate permissions to visit this view",
      "yellow",
    );

    return <Navigate to="/" replace />;
  }

  return (
    <DesktopSyncProvider>
      <Header />
      <ProtectedRouteContent />
      <GPDRModal />
    </DesktopSyncProvider>
  );
}
